import React from 'react';
import './App.css';
import { PartialTheme, ThemeProvider } from "@fluentui/react";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { MsalProvider } from "@azure/msal-react";
import { reactPlugin } from './applicationInsights';
import { msalInstance } from "./authClient";
import { AuthProvider } from "./providers/auth";
import { AppRoutes } from "./pages/Routes";
import {
    BOARD_CONTEXT_ID_STORAGE_KEY,
    BOARD_CONTEXT_TYPE_STORAGE_KEY,
    BOARD_PENDING_QUERY_SELECTION_STORAGE_KEY,
    BOARD_SELECTION_STORAGE_KEY,
    LEGACY_SITE_STORAGE_KEY,
    parseSelectionFromSearch,
    serializeSelectionForStorage,
} from "./helpers/boardSelection";
import { bootstrapAvantCareEnvHeader, getQueryOrgName } from "./helpers/avantCareEnv";

const theme: PartialTheme = {
    palette: {
        themePrimary: '#f37037',
        themeLighterAlt: '#fef9f7',
        themeLighter: '#fde7de',
        themeLight: '#fbd3c1',
        themeTertiary: '#f7a785',
        themeSecondary: '#f4804f',
        themeDarkAlt: '#da6532',
        themeDark: '#b8552a',
        themeDarker: '#883f1f',
        neutralLighterAlt: '#faf9f8',
        neutralLighter: '#f3f2f1',
        neutralLight: '#edebe9',
        neutralQuaternaryAlt: '#e1dfdd',
        neutralQuaternary: '#d0d0d0',
        neutralTertiaryAlt: '#c8c6c4',
        neutralTertiary: '#b4b4b3',
        neutralSecondary: '#9c9b9a',
        neutralPrimaryAlt: '#848382',
        neutralPrimary: '#242323',
        neutralDark: '#545352',
        black: '#3c3b3a',
        white: '#ffffff',
    },

    semanticColors: {
        successIcon: '#6bb700',
        errorIcon: '#c50f1f',
        warningIcon: '#ffaa44',
        infoIcon: '#565656',

        successBackground: '#6bb70030',
        errorBackground: '#c50f1f30',
        warningBackground: '#ffaa4430',
        infoBackground: '#56565630',
    }
};

library.add(...(Object.values(fas) as IconDefinition[]));

function App() {
    // Resolve and persist the environment before any auth redirect drops the original query string.
    const urlParams = new URLSearchParams(window.location.search);
    const resolvedOrgName = bootstrapAvantCareEnvHeader();
    if (!getQueryOrgName() && resolvedOrgName) {
        urlParams.set('orgname', resolvedOrgName);
        const nextSearch = urlParams.toString();
        const nextRelativeUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
        const currentRelativeUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        if (nextRelativeUrl !== currentRelativeUrl) {
            window.history.replaceState(null, '', nextRelativeUrl);
        }
    }

    const explicitSelection = parseSelectionFromSearch(window.location.search);
    if (explicitSelection) {
        localStorage.setItem(
            BOARD_PENDING_QUERY_SELECTION_STORAGE_KEY,
            serializeSelectionForStorage(explicitSelection)
        );
        localStorage.setItem(
            BOARD_SELECTION_STORAGE_KEY,
            serializeSelectionForStorage(explicitSelection)
        );
        localStorage.setItem(BOARD_CONTEXT_TYPE_STORAGE_KEY, explicitSelection.selectionMode);
        localStorage.setItem(BOARD_CONTEXT_ID_STORAGE_KEY, explicitSelection.contextId);
        if (explicitSelection.selectionMode === 'site') {
            localStorage.setItem(LEGACY_SITE_STORAGE_KEY, explicitSelection.contextId);
        } else {
            localStorage.removeItem(LEGACY_SITE_STORAGE_KEY);
        }
    }

    return (
        <div id="react-app" className="App">
            <ThemeProvider theme={theme}>
                <style>
                    {`html {
                        --color-successIcon: ${theme.semanticColors?.successIcon};
                        --color-warningIcon: ${theme.semanticColors?.warningIcon};
                        --color-errorIcon: ${theme.semanticColors?.errorIcon};
                        --color-infoIcon: ${theme.semanticColors?.infoIcon};
                        
                        --color-successBackground: ${theme.semanticColors?.successBackground};
                        --color-warningBackground: ${theme.semanticColors?.warningBackground};
                        --color-errorBackground: ${theme.semanticColors?.errorBackground};
                        --color-infoBackground: ${theme.semanticColors?.infoBackground};
                    }`}
                </style>
                <MsalProvider instance={msalInstance}>
                    <AuthProvider>
                        <AppRoutes />
                    </AuthProvider>
                </MsalProvider>
            </ThemeProvider>
        </div>
    );
}

export default withAITracking(reactPlugin, App);
